import { FreeDataVO } from '../../../vo/core/freedata.vo';
import { BASIC_ENTITY_TYPE } from '../../../model/enums';
import { DeletedEmsCmd } from '../../em/deleted-ems.cmd';
import { Model } from '../../../model/model';
import { DataPack, EntityModificationTrace, FreeData, FreeDataValue } from '@wizeoo/akm-message-lib';
import { FreedataDataload } from '../../../vo/core/freedata.dataload';
import { take } from 'rxjs';
import { ViewData } from '../../../model/view.data';
import { FreedataValueVO } from '../../../vo/core/freedatavalue.vo';
import { FreedataCmd } from './freedata.cmd';
import { FreedataCountCmd } from './freedatacount.cmd';
import { FreeDataValueCmd } from './freedatavalue.cmd';
import { AkmBDD, BddFreedata, LocalFreedata } from '@wizeoo/akm-worker-lib';
import { AllkeyWebWorker } from '../../../manager/worker/allkey.web.worker';
import { WizCommand, WizGroupCommand } from '@wizeoo/akm-component-lib';
import { EM_ACTION} from '@wizeoo/akm-compute-lib';

/** service de gestion des FD / FDVs. Synchro cache local et appels par incréments */
export class FreedataSyncService {

  protected model: Model = null;

  constructor(model: Model) {
    this.model = model;
  }

  // pack de chargement
  public freedataLoad: FreedataDataload = null; 

  /** la clé du pack, domain + BU */
  public get key(): string {
    return this.model.domainCode + ViewData.KEY_SEPARATOR +  this.model.businessUnitCode;
  }

  public resetAll(): void {
    this.freedataLoad = null;
    this.model.freedataEngine.setFreedataMap(null);
    this.model.freedataEngine.setFreeDataCollection(null);
  }

  /**
   * chargement des FDs depuis le cache local & synchro remote
   * @param callback pour retour du pool freedatas: []
   * @param force si actif, force le rechargement (sur import)
   * @returns freedatas: []
   */
  public findFreedata(callback: Function, force: boolean = false): void {
    
    const key: string = this.key;
    if (!!this.freedataLoad) { // j'en ai déjà un, je contrôle sa clé (init multi BU...)   
      if (this.freedataLoad.uniqueKey !== key) {
        this.freedataLoad = null; // j'en ai plus...
      }
    }
    
    if (!force) { // je suis pas en train de forcer
      if (this.freedataLoad?.state === FreedataDataload.COMPLETE) { // et il est déjà complet
        callback(this.freedataLoad.localFds); // je renvoie direct le pack
        return;
      }
    }

    if (!this.freedataLoad || force) { // pas de pack, ou demande de forçage
      this.freedataLoad = new FreedataDataload(this.model.domainCode, this.model.businessUnitCode);
      this.freedataLoad.callbacks.push(callback); //  = callback;

      // chargement local
      AkmBDD.getInstance().doFindBuFreedatas(key)
        .pipe(take(1))
        .subscribe((bddFDs: BddFreedata[]) => {
        
        const local: LocalFreedata = {key: key, revisionNumber: -1, freedatas: []}; // objet localFD pour manipulation simple

        // TODO gestion des revNumbers par FD    
        if(!!bddFDs) {
          // console.log('fds', bddFDs);
          bddFDs.forEach((fd: BddFreedata) => {
            local.freedatas.push(fd.value);
            if(fd.value.revisionNumber > local.revisionNumber) { // collecte du numRev Max
              local.revisionNumber = fd.value.revisionNumber;
            }
          });
        }

        if (local.revisionNumber >= 0) { // alimentation du numRev
          this.freedataLoad.revisionNumber = local.revisionNumber;
        }
        this.freedataLoad.localFds = new Map(local.freedatas.map(item => [item.code, item])); // alimentation du jeu local dans le pack

        // console.log('fd service', this.freedataLoad.revisionNumber);
        new FreedataCountCmd(this.model).count(this.freedataLoad, null, this.backFromCount.bind(this)).execute();
      });
    } else { // j'ai déjà un pack en cours, non COMPLETE... J'attends
      this.freedataLoad.callbacks.push(callback); //  = callback;
    }
  }

  /**
   * retour de count, éventuel appel remote
   * @param cmd 
   */
  private backFromCount(cmd: FreedataCmd): void {
    this.remoteStep(cmd.dataLoad);
  }

  /**
   * pile d'appels remote
   * @param dataload 
   */
  private remoteStep(dataload: FreedataDataload): void {
    // console.log('FDS remoteStep', dataload.nbFds, dataload.nbFdvs, dataload.nbDeleteds);
    const cmds: WizCommand<any>[] = [];

    if (dataload.nbFds > 0) { // des FDs à mettre à jour
      cmds.push(new FreedataCmd(this.model).findFromRevNumber(dataload)); // uniquement les entêtes... les FDV sont gérés à part
    }
    if (dataload.nbFdvs > 0) { // des FDVs à mettre à jour
      cmds.push(new FreeDataValueCmd(this.model).findFromRevNumber(dataload)); // les FDVs, en masse
    }

    if (dataload.nbDeleteds > 0) { // et peut-être des deletes
      cmds.push(new DeletedEmsCmd(this.model).findFreedata(dataload));
    }

    if (cmds.length) {
      const grpCmd = new WizGroupCommand().configure(cmds, this.backFromFind.bind(this), true);
      grpCmd.carrier = dataload;
      grpCmd.execute();
    } else {
      // console.log('FD remote', dataload);
      this.endService(dataload);
    }
  }

  private backFromFind(cmd: WizGroupCommand): void {
    this.endService(cmd.carrier);
  }

  /** alimentation des collections et merge local */
  private endService(dataLoad: FreedataDataload): void {
    this.freedataLoad = dataLoad;
    this.freedataLoad.state = FreedataDataload.COMPLETE; // ok, chargé

    const voMap: Map<string, FreeDataVO> = new Map(); // alimentation des FDVOs
    const updatedFDSet: Map<string, FreeData> = new Map<string, FreeData>(); // lister les FDs modifiés

    const toDieFDSet: Map<number, EntityModificationTrace> = new Map(); // je vois des gens qui sont morts..
    if(!!this.freedataLoad.deletedFds) { // new Set(this.freedataLoad.deletedFds); 
      this.freedataLoad.deletedFds.forEach((emt: EntityModificationTrace) => {
        toDieFDSet.set(emt.id, emt);
      });
    }
    
    const toDieFDVSet: Map<number, EntityModificationTrace> = new Map(); // je vois des gens qui sont morts..
    if(!!this.freedataLoad.deletedFdvs) { // new Set(this.freedataLoad.deletedFdvs); // je vois des gens qui sont morts.. 
      this.freedataLoad.deletedFdvs.forEach((emt: EntityModificationTrace) => {
        toDieFDVSet.set(emt.id, emt);
      });
    }

    this.freedataLoad.localFds.forEach((fd: FreeData) => { // on garde les pas morts
      if (!toDieFDSet.has(fd.id)) { // dans la map des morts, au revoir
        voMap.set(fd.code, this.model.freedataEngine.newFreedataVO(fd));
      }
    });

    this.freedataLoad.freedatas.forEach((fd: FreeData) => { // on écrase avec les updates, on crée les nouveaux
      const exist: FreeDataVO =  voMap.get(fd.code); 
      if(!!exist) {
        fd.freeDataValues = exist.source.freeDataValues; // On préserve les FDVs
      }
      voMap.set(fd.code, this.model.freedataEngine.newFreedataVO(fd));
      updatedFDSet.set(fd.code, fd);
    });

    // ajout / merge des FDVs
    this.freedataLoad.freedataValues.forEach((fdv: FreeDataValue) => { // on écrase avec les updates, on crée les nouveaux
      const fdvvo: FreedataValueVO = this.model.freedataEngine.newFreedataValueVO(fdv);
      const fdvo: FreeDataVO = voMap.get(fdvvo.freeDataCode);
      if(!!fdvo) {
        // console.log('Add or Update on FDV: ', fdvo.code, fdvvo.code);
        fdvo.addFreeDataValue(fdvvo, true);
        updatedFDSet.set(fdvo.code, fdvo.source);
      }
    });

    // suppression des FDV deleted
    if(toDieFDVSet.size > 0) { // des FDVs sont morts...
      const fdvMapByID: Map<number, FreedataValueVO> = new Map(); // création de la map des FDV encore existant par ID
      [...voMap.values()].forEach((fdvo: FreeDataVO) => {
        fdvo.getValuesVO().forEach((fdvvo: FreedataValueVO) => {
          fdvMapByID.set(fdvvo.id, fdvvo);
        });
      });
      
      [...toDieFDVSet.keys()].forEach((dieID: number) => {
        const toDieVO: FreedataValueVO = fdvMapByID.get(dieID);
        //console.log('toDie', toDieVO);
        if(!!toDieVO) {
          voMap.get(toDieVO.freeDataCode).removeFreeDataValue(toDieVO, true);
          updatedFDSet.set(toDieVO.freeDataCode, voMap.get(toDieVO.freeDataCode).source);
        }
      });

    }

    const sourceArr: Map<string, FreeData> = new Map(); // la collec des FDs encore vivants, et les nouveaux, pour alimentation cache local
    // construction de la map à sauvegarder
    [...voMap.values()].forEach((fdvo: FreeDataVO) => {
      sourceArr.set(fdvo.code, fdvo.source);
    });

    this.freedataLoad.localFds = sourceArr; // on met à jour le dataLoad, il est clean

    
    this.model.freedataEngine.setFreeDataCollection(Array.from(voMap.values())); // on alimente le modèle
    this.model.freedataEngine.setFreedataMap(voMap);
    // console.log('FD endService', this.model.freedataMap );
    


    if (this.freedataLoad.remoteRevisionNumber > this.freedataLoad.revisionNumber) {
      
      // mise à jour du dataLoad
      this.freedataLoad.revisionNumber = this.freedataLoad.remoteRevisionNumber; // sync directe
      this.freedataLoad.toPersist = updatedFDSet;
      this.freedataLoad.toDie = toDieFDSet;
      this.writeBDD(this.freedataLoad);
    }
    this.resolveCallbacks();

  }

  /** on remercie les callbacks, en leur passant le pool local (source) */
  private resolveCallbacks(): void {
    this.freedataLoad.callbacks.forEach((f: Function) => {
      f(this.freedataLoad.localFds);
    })
    this.freedataLoad.callbacks = []; // on rince les callbacks;
  }

  /** persistence local par le serviceWorker */
  private writeBDD(freedataload: FreedataDataload): void {
    AllkeyWebWorker.storeFreedata(freedataload);
  }

  /**
   * suppression d'un FD depuis la vue FreedataView
   */
  public removeLocalFreedata(fd: FreeDataVO): void {
    
    this.freedataLoad.toDie = new Map();
    this.freedataLoad.toDie.set(fd.id, null);
    this.freedataLoad.toPersist = null;

    this.freedataLoad.localFds.delete(fd.code); // suppression du dataLoad

    AllkeyWebWorker.storeFreedata(this.freedataLoad); // suppression en BDD
  }

  /**
   * creation d'un FD depuis la vue FreedataView
   */
    public addLocalFreedata(fd: FreeDataVO): void {
    
      this.freedataLoad.toDie = null;
      this.freedataLoad.toPersist = new Map();
      this.freedataLoad.toPersist.set(fd.code, fd.source);
  
      this.freedataLoad.localFds.set(fd.code, fd.source); // ajout du dataLoad
  
      AllkeyWebWorker.storeFreedata(this.freedataLoad); // ajout en BDD
    }


  /** 
    * sur retour de commit composite, mise à jour du cache et des dataload chargés
    * @param pack 
    */
  public eatRemote(pack: DataPack): void 
  {
    let localMap: Map<string, FreeData> = this.freedataLoad.localFds; // les fds stockées en indexDB
    // map des FDs modifiés
    const toUpdateFD: Map<string, FreeData> = new Map<string, FreeData>();
    // map des FDs à supprimer, par ID
    const toDieFD: Map<number, EntityModificationTrace> = new Map(); 

    // gestion des deleted en premier, en cas de delete recreate
    if(pack.deletedFreedataAndValues) {

      pack.deletedFreedataAndValues.forEach((emt: EntityModificationTrace) => {
        if(emt.clas === BASIC_ENTITY_TYPE.FREEDATA) {
          toDieFD.set(emt.id, emt); // mémorisation pour nettoyer en cache local
          localMap.delete(emt.key); // suppression du dataload
        } else if(emt.clas === BASIC_ENTITY_TYPE.FREEDATAVALUE) {

          let fd: FreeData = localMap.get(emt.key);
          if(!!fd) {
            delete fd.freeDataValues[emt.mkey];
            // console.log('fdService deleteFDV', emt.mkey);
            // signaler la mise à jour sur le FD pour persistence en cache local
            toUpdateFD.set(fd.code, fd);
          }
        }

      });

    }

    if(pack.inFreedata) { // mise à jour version et numRev sur le FD en cours
      const locFD: FreeData = localMap.get(pack.inFreedata.code);
      if(locFD) { // on garde les freedataValues (pour l'instant)
        pack.inFreedata.freeDataValues = locFD.freeDataValues;
      }
      localMap.set(pack.inFreedata.code, pack.inFreedata );
      toUpdateFD.set(pack.inFreedata.code, pack.inFreedata);
    }   
    
    if(pack.remoteFreedata) { // updates / create sur FD
      pack.remoteFreedata.forEach((fd: FreeData) => {
        const locFD: FreeData = localMap.get(fd.code);
        if(locFD) { // on garde les freedataValues (pour l'instant)
          fd.freeDataValues = locFD.freeDataValues;
        }
        localMap.set(fd.code, fd );
        toUpdateFD.set(fd.code, fd);
      });
    }

    if(pack.inFreedataValues) { // mise à jour des blvs
      pack.inFreedataValues.forEach((fdv: FreeDataValue) => {
        // console.log('fdService inFDV', fdv);
        
        const locFD: FreeData = localMap.get(fdv.freeDataCode);
        if(locFD) { // on écrase
          if(fdv.operation === EM_ACTION.DELETE) {
            delete locFD.freeDataValues[fdv.code];
            // console.log('fdService inFDV deleteFDV', fdv);
          } else {
            locFD.freeDataValues[fdv.code] = fdv;
          }
          
        }

        toUpdateFD.set(locFD.code, locFD);
      });
    }  

    if(pack.remoteFreedataValues) { // mise à jour des blvs
      pack.remoteFreedataValues.forEach((fdv: FreeDataValue) => {
        const locFD: FreeData = localMap.get(fdv.freeDataCode);
   
        if(locFD) { // on écrase
          if(fdv.operation === EM_ACTION.DELETE) {
            delete locFD.freeDataValues[fdv.code];
            // console.log('fdService remoteFreedataValues deleteFDV', fdv);
          } else {
            locFD.freeDataValues[fdv.code] = fdv;
          }
        }
        toUpdateFD.set(locFD.code, locFD);
      });
    }

    // synchro du dataload
    this.freedataLoad.localFds = localMap; 

    if(!pack.crossGenerated) { // crossVision on ne monte pas la version, on a pas collecté ce qui à pu être généré
      this.freedataLoad.remoteRevisionNumber = pack.freedataRevisionNumber;
      this.freedataLoad.revisionNumber = this.freedataLoad.remoteRevisionNumber;
    }

    
    this.freedataLoad.toPersist = toUpdateFD;
    this.freedataLoad.toDie = toDieFD;
    this.writeBDD(this.freedataLoad);

  }
}
